<script setup>
import {computed, onMounted} from "vue";
import {DEFAULT_ACQUIRED_COINS_PER_PAGE, useAcquiredCoinsStore} from "@/store/acquired-coins";
import usePagination from "@/composables/pagination";
import {formatAsCalendarDay} from "@/tools/date-time.tools";
import {openImagePreview} from "@/composables/image-preview";
import EmptyCard from "@/components/shared/EmptyCard.vue";
import MainPreloader from "@/components/shared/MainPreloader.vue";
import TablePager from "@/components/shared/TablePager.vue";

const coinsStore = useAcquiredCoinsStore();
const pagination = usePagination(coinsStore, DEFAULT_ACQUIRED_COINS_PER_PAGE, false);
const items = computed(() => pagination.pageData.value);

async function fillData() {
  await coinsStore.clearData();
  await pagination.initLoad();
}

onMounted(fillData);
</script>

<template>
  <section class="coinsView">
    <h2 class="coinsView__title">Монеты</h2>
    <p class="coinsView__description">Монеты из ваших оплаченных покупок.</p>
    <MainPreloader v-if="coinsStore.loading" position="fixed" :size="80" :stroke="3"/>
    <EmptyCard v-if="!coinsStore.loading && !items?.length"/>
    <template v-else-if="items?.length">
      <div class="coinsView__grid">
        <article v-for="coin in items" :key="coin.id" class="coinsView__card">
          <img
              class="coinsView__image"
              :src="coin.img_url || require('@/assets/img/preview_placeholder.jpg')"
              :alt="coin.name || 'Монета'"
              @click="openImagePreview(coin.img_url)"
              @error="(event) => event.target.src = require('@/assets/img/preview_placeholder.jpg')"
          >
          <div class="coinsView__content">
            <h3>{{ coin.name || 'Монета' }}</h3>
            <p v-if="coin.series || coin.number" class="coinsView__number">
              <template v-if="coin.series">Серия {{ coin.series }}</template>
              <template v-if="coin.series && coin.number"> · </template>
              <template v-if="coin.number">№ {{ coin.number }}</template>
            </p>
            <p class="coinsView__purchase">Покупка от {{ formatAsCalendarDay(coin.purchased_at) }}</p>
            <p class="coinsView__order">Заказ № {{ coin.order_number }}</p>
          </div>
        </article>
      </div>
      <TablePager
          v-if="pagination.countPages.value > 1 || pagination.hasMoreData.value"
          :pagination="pagination"
      />
    </template>
  </section>
</template>

<style scoped lang="scss">
.coinsView {
  display: flex;
  flex-direction: column;
  gap: 14px;
  text-align: left;

  &__title {
    margin: 0;
    color: var(--cd-text);
    font-size: clamp(22px, 3vw, 30px);
    font-weight: 500;
  }

  &__description {
    margin: -6px 0 0;
    color: var(--cd-text-muted);
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 14px;
  }

  &__card {
    overflow: hidden;
    border-radius: 20px;
    background: var(--cd-card-bg);
  }

  &__image {
    display: block;
    width: 100%;
    aspect-ratio: 1.2;
    object-fit: cover;
    cursor: zoom-in;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 16px;
  }

  h3,
  p {
    margin: 0;
  }

  h3 {
    color: var(--cd-text);
    font-size: 18px;
    font-weight: 500;
  }

  &__number {
    color: var(--cd-base-green);
  }

  &__purchase,
  &__order {
    color: var(--cd-text-muted);
    font-size: 14px;
  }
}
</style>
